import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Heart } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";

const TIERS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    sub: "+ $9.99 one-time setup",
    cta: "Start Free",
    features: [
      "Basic wedding website",
      "Custom URL (lockedinforever.com/yourname)",
      "RSVP manager (up to 50 guests)",
      "10 photos in Memory Vault",
      "1 invitation type",
    ],
  },
  {
    id: "premium",
    name: "Premium Couple",
    price: "$19",
    sub: "per month",
    cta: "Choose Premium",
    features: [
      "Unlimited guests & RSVPs",
      "Premium templates",
      "Unlimited photo & video memories",
      "Guest QR upload portal",
      "All invitation types",
      "Dress & Tux tracker",
      "Vendor & budget tracker",
    ],
    featured: true,
  },
  {
    id: "luxury",
    name: "Luxury",
    price: "$49",
    sub: "per month",
    cta: "Choose Luxury",
    features: [
      "Everything in Premium",
      "Custom domains",
      "Voice messages & video blessings",
      "Live wedding day broadcast",
      "Downloadable keepsake album",
      "AI vow & speech assistant",
      "Vendor concierge",
    ],
  },
  {
    id: "pro",
    name: "Planner / Vendor Pro",
    price: "$99",
    sub: "per month",
    cta: "For Planners",
    features: [
      "White-label wedding sites",
      "Unlimited client portals",
      "Branded planner dashboard",
      "CRM & analytics",
      "Vendor marketplace listing",
      "Subscription billing tools",
    ],
  },
];

export default function PricingPage() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const [promoCode, setPromoCode] = useState(() => (new URLSearchParams(window.location.search).get("promo") || "").toUpperCase());
  const [promo, setPromo] = useState(null);
  const [validating, setValidating] = useState(false);

  async function validatePromo(plan_id) {
    if (!promoCode) return null;
    setValidating(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promoCode, plan_id });
      setPromo(data);
      const plans = (data.applies_to_plans || []).map((p) => p[0].toUpperCase() + p.slice(1)).join(", ");
      toast.success(`${data.code} applied: ${data.type === "percent" ? data.value + "% off" : "$" + data.value + " off"}${plans ? ` on ${plans}` : ""}`);
      return data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
    finally { setValidating(false); }
  }

  const auto = React.useRef(false);
  useEffect(() => { if (promoCode && !auto.current) { auto.current = true; validatePromo(null); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function pickTier(tier) {
    let pr = promo;
    if (promoCode && !pr && tier.id !== "free") { pr = await validatePromo(tier.id); if (!pr) return; }
    if (pr?.applies_to_plans?.length && tier.id !== "free" && !pr.applies_to_plans.includes(tier.id)) {
      return toast.error(`${pr.code} only works on the ${pr.applies_to_plans.map((p) => p[0].toUpperCase() + p.slice(1)).join(", ")} plan`);
    }
    if (!user) {
      toast.error("Please sign in first");
      nav(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    if (tier.id === "free") {
      // Mark plan_selected=true server-side, then continue
      setBusy("free");
      try {
        const { data } = await api.post("/me/select-plan", { plan: "free" });
        if (typeof setUser === "function") setUser(data);
        toast.success("Free plan activated");
        nav(data.has_wedding ? "/dashboard" : "/onboarding");
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setBusy(null); }
      return;
    }
    setBusy(tier.id);
    try {
      const payload = { plan_id: tier.id, origin_url: window.location.origin };
      if (promoCode) payload.promo_code = promoCode;
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(null); }
  }

  return (
    <div className="bg-[var(--bg)] min-h-screen">
      <Seo
        title="Pricing — Free to Start, Luxury When You're Ready"
        description="Simple, luxurious pricing. Free to begin. Premium ($19), Luxury ($49), and Pro ($99) tiers unlock advanced features. Lifetime photos on every paid plan."
        path="/pricing"
        keywords={["wedding website pricing", "wedding website cost", "wedding planner platform pricing", "luxury wedding software"]}
      />
      <Navbar />
      <section className="container-wide px-6 md:px-12 py-20 text-center">
        <p className="font-script text-[var(--gold)] text-3xl mb-2">Pricing</p>
        <h1 className="font-serif font-light text-5xl md:text-7xl mb-4">Simple. Luxurious. Yours.</h1>
        <p className="text-[var(--muted)] max-w-xl mx-auto">Free to begin. Upgrade when you're ready for the full luxury experience.</p>
        <div className="mt-6 inline-flex items-center gap-2 bg-[var(--bg-warm)] rounded-full px-4 py-2 text-sm">
          <Heart className="w-4 h-4 text-[var(--gold)]" />
          <span><strong className="text-[var(--ink)]">Lifetime photos</strong> included on every paid tier — your memories never expire.</span>
        </div>

        <div className="mt-8 flex justify-center">
          <div className="flex items-center gap-2 bg-white border border-[var(--line)] rounded-full px-2 py-1 max-w-md w-full">
            <input
              data-testid="promo-input"
              value={promoCode}
              onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromo(null); }}
              placeholder="Have a promo code?"
              className="flex-1 px-4 py-2 bg-transparent outline-none text-sm font-mono"
            />
            <button
              data-testid="promo-apply"
              onClick={() => validatePromo(null)}
              disabled={!promoCode || validating}
              className="text-xs uppercase tracking-wider px-4 py-2 rounded-full bg-[var(--ink)] text-white disabled:opacity-50"
            >
              {validating ? "Checking…" : promo ? "Applied ✓" : "Apply"}
            </button>
          </div>
        </div>
      </section>

      <section className="container-wide px-6 md:px-12 pb-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TIERS.map((t) => (
            <div key={t.id} className={`tier-card ${t.featured ? "featured" : ""}`} data-testid={`tier-${t.id}`}>
              {t.featured && <div className="mb-4 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[var(--gold)]"><Sparkles className="w-3 h-3" /> Most Loved</div>}
              <h3 className="font-serif text-2xl mb-2">{t.name}</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-serif text-5xl tier-price">{t.price}</span>
                <span className="text-xs text-[var(--muted)]">{t.sub}</span>
              </div>
              <ul className="space-y-2 my-6 text-sm">
                {t.features.map((f) => <li key={f} className="flex items-start gap-2"><Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.featured ? "text-[var(--gold)]" : "text-[var(--gold)]"}`} />{f}</li>)}
              </ul>
              <button data-testid={`select-${t.id}`} onClick={() => pickTier(t)} disabled={busy === t.id} className={t.featured ? "btn-gold w-full" : "btn-outline-gold w-full"}>
                {busy === t.id ? "Redirecting…" : t.cta}
              </button>
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
